import * as React from "react";
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { cs } from '@shared/utils/utils-classname';
import './global-search.scss';
import { OutsideClick } from '@shared/ui-react/react-outside-click-handler';


export interface ISearchResult {
    group: string;
    name: string;
    uuid: string;
    iconId?: string;
    path?: string[];
    provider?: ISearchProvider;
}


export interface ISearchItemAction {
    label: string;
    action: (item: ISearchResult) => any;
}


export interface ISearchProvider {
    label: string;
    group: string;
    doSearch: (query: string, limit: number) => Promise<ISearchResult[]>;
    getItemThumbnail: (item: ISearchResult) => any;
    getItemActions: (item: ISearchResult) => ISearchItemAction[];
}

let lastQuery: string = null;
let lastProviderIndex: number = null;
let lastItemIndex: number = null;

@observer
export class GlobalSearch extends React.Component<{
    providers: ISearchProvider[],
    limit: number,
    onClose: () => any
}> {
    @observable input: string = lastQuery;
    @observable providerIndex: number = lastProviderIndex;
    @observable focusedItemIndex: number = lastItemIndex;
    @observable focusedItemActionIndex: number = 0;
    @observable results: ISearchResult[] = [];
    @observable isSearching = false;
    isInitialLoad = true;
    inputEl: HTMLInputElement = null;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown);
        if (this.input?.length) {
            this.inputEl.select();
            this.handleQueryChange(this.input);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown)
        lastQuery = this.input;
        lastProviderIndex = this.providerIndex;
        lastItemIndex = this.focusedItemIndex;
    }

    handleKeyDown = (event) => {
        if ([9, 38, 40, 13, 37, 39].includes(event.keyCode)) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (event.keyCode === 9) {
            // tab
            if (this.props.providers.length === 1) return;

            if (this.providerIndex === null) {
                this.providerIndex = 0;
            } else {
                if (this.providerIndex === this.props.providers.length - 1) {
                    this.providerIndex = null;
                } else {
                    this.providerIndex += 1;
                }
            }
            this.handleQueryChange(this.input);
        }
        if (event.keyCode === 38) {
            this.focusedItemIndex = Math.max(0, this.focusedItemIndex - 1);
            this.focusedItemActionIndex = 0;
        }
        if (event.keyCode === 40) {
            this.focusedItemIndex = Math.min(this.results.length - 1, this.focusedItemIndex + 1);
            this.focusedItemActionIndex = 0;
        }
        if (event.keyCode === 13) {
            // enter
            const actions = this.results[this.focusedItemIndex].provider.getItemActions(this.results[this.focusedItemIndex]);
            actions[this.focusedItemActionIndex].action(this.results[this.focusedItemIndex]);
        }
        if (event.keyCode === 37) {
            // left
            this.focusedItemActionIndex = Math.max(0, this.focusedItemActionIndex - 1);
        }

        if (event.keyCode === 39) {
            // right
            const actions = this.results[this.focusedItemIndex].provider.getItemActions(this.results[this.focusedItemIndex]);
            this.focusedItemActionIndex = utils.clamp(0, this.focusedItemActionIndex + 1, actions.length - 1);
        }

        this.scrollToFocused();
    }

    scrollToFocused = () => {
        setTimeout(() => {
            const el = document.querySelector('.search-result-item.focused');
            if (el) {
                el.scrollIntoView({
                    block: 'center',
                    inline: 'start'
                });
            }
        }, 20);
    }

    render() {
        const groupedByType = Object.entries(_.groupBy(this.results, 'group'));

        return (
            <OutsideClick onOutsideClick={this.onOutsideClick}>
                <div id='global-search-wrapper' className="card padded flex-column gap-half">
                    <div style={{ position: 'relative' }}>
                        <i className="iki iki-search" style={{ position: 'absolute', left: 12, top: 12, fontSize: '1.6em', opacity: 0.5 }}></i>
                        <input ref={el => this.inputEl = el} autoFocus={true} type="text" style={{ fontSize: '1.5em', padding: 10, height: 'auto', paddingLeft: 50, width: '100%' }} value={this.input} onChange={e => this.handleQueryChange(e.currentTarget.value)}/>
                    </div>

                    { this.props.providers.length > 1 &&
                        <div className="flex-row gap-half global-search-providers">
                            <div key="all" className={cs('badge', { focused: this.providerIndex === null })} onClick={() => this.setProviderIndex(null)}>{__('js_global_search.all_group')}</div>
                            {this.props.providers.map((provider, index) => {
                                return <div key={index} className={cs('badge ellipsis', { focused: this.providerIndex === index })} onClick={() => this.setProviderIndex(index)}>{provider.label}</div>
                            })}
                        </div>
                    }
                    <div style={{ fontStyle: 'italic', opacity: 0.7 }}>
                        {this.props.providers.length > 1 && <span>[tab] {__('js_global_search.tab_to_change_group')},</span> } <span>[⇅⇆] {__('js_global_search.arrows_to_navigate')}, [esc] {__('js_global_search.esc_to_close')}</span>
                    </div>

                    <div className={cs('search-results-wrapper', { 'overlay-loading': this.isSearching })}>
                        {!this.input?.length && <div className="padded text-center" style={{ fontSize: '1.1em', margin: 'auto' }}>{__('js_global_search.type_to_find_anything')}</div>}

                        {this.results.length === 0 ? <>
                            {this.input && !this.isSearching && <div className="padded text-center" style={{ fontSize: '1.1em', margin: 'auto' }}>{__('js_global_search.no_results')}</div> }
                        </> : groupedByType.map(grp => {
                            return <div key={grp[0]} className="flex-column search-result-group">
                                <header>{this.props.providers.find(p => p.group === grp[0])?.label}</header>
                                {grp[1].map((item, i) => {
                                    const isFocused = this.focusedItemIndex === this.results.indexOf(item);
                                    const actions = item.provider.getItemActions(item);

                                    return <div className={cs('search-result-item', { focused: isFocused })} key={item.uuid} onClick={() => this.selectItem(item)}>
                                        <div className="thumbnail">
                                            {item.provider.getItemThumbnail(item)}
                                        </div>
                                        <div className="flex-column" style={{ gap: 5 }}>
                                            <div className="flex-row name allipsis">{item.name}</div>
                                            {item.path?.length > 0 &&
                                                <div style={{ fontSize: '0.9em', opacity: 0.7 }}>{item.path?.join(' / ')}</div>
                                            }
                                            {isFocused &&
                                                <div className="flex-row actions gap-half">
                                                    {actions.map((action, index) => {
                                                        return <button key={index} onClick={() => action.action(item)} className={cs('narrow', { focused: this.focusedItemActionIndex === index })}>{action.label}</button>
                                                    })}
                                                </div>
                                            }
                                        </div>
                                    </div>
                                })}
                            </div>
                        })}
                    </div>
                </div>
            </OutsideClick>
        );
    }

    setProviderIndex = (index: number) => {
        this.providerIndex = index;
        this.handleQueryChange(this.input);
    }

    selectItem = (item: ISearchResult) => {
        // dont select items by uuid since same item can appear multiple times in different groups
        if (item) {
            this.focusedItemIndex = this.results.indexOf(item);
            this.focusedItemActionIndex = 0;
        }
    }

    handleQueryChange = async (query: string) => {
        if (!this.isInitialLoad) {
            this.focusedItemIndex = 0;
        }
        this.isInitialLoad = false;
        this.focusedItemActionIndex = 0;

        this.input = query ? query.trimStart().toLowerCase() : null;
        if (!this.input || this.input.length === 0) {
            this.results = [];
            return;
        }

        this.isSearching = true;
        if (this.abort) {
            this.abort.abort();
            this.abort = null;
        }
        this.abort = new AbortController();
        try {
            this.results = await this.doSearch();
            this.isSearching = false;
        } catch (e) {
            if (e.reason !== 'aborted') {
                console.error('search error', e);
            }
        }
        this.abort = null;

        this.scrollToFocused();
    }

    abort: AbortController = null;

    onOutsideClick = e => {
        if (e.target.closest('.js-global-search-toggle')) {
            return;
        }
        this.props.onClose();
    }

    private doSearch = (): Promise<ISearchResult[]> => {
        return new Promise(async (done, fail) => {
            this.abort.signal.addEventListener("abort", () => {
                fail({ reason: 'aborted' });
            });

            let results: ISearchResult[] = [];
            for (let provider of (this.providerIndex === null ? this.props.providers : [this.props.providers[this.providerIndex]])) {
                try {
                    const resp = (await provider.doSearch(this.input.trim(), this.providerIndex === null ? this.props.limit : 50));
                    resp.forEach(i => i.provider = provider);
                    results.push(...resp);
                } catch (e) {
                    console.error('search provider error', provider.label, e);
                }
            }
            done(results);
        });
    }
}
